import React, { useState } from "react";
import { Play, X } from "lucide-react";
import { FILMS } from "../mock/data";
import useReveal from "../hooks/useReveal";

function thumb(id) {
  return `https://img.youtube.com/vi/${id}/maxresdefault.jpg`;
}

function FilmCard({ film, i, onPlay }) {
  const { ref, inView } = useReveal();
  return (
    <button
      ref={ref}
      onClick={() => onPlay(film)}
      style={{ animationDelay: `${i * 0.1}s` }}
      className={`reveal ${inView ? "in-view" : ""} group relative block overflow-hidden text-left w-full`}
    >
      <div className="relative h-60 overflow-hidden bg-[#0a0a0a]">
        <img
          src={thumb(film.youtube)}
          alt={film.title}
          onError={(e) => {
            e.currentTarget.src = `https://img.youtube.com/vi/${film.youtube}/hqdefault.jpg`;
          }}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-black/40 group-hover:bg-black/25 transition-colors" />
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="w-16 h-16 flex items-center justify-center bg-baja-red/90 group-hover:scale-110 transition-transform animate-glow">
            <Play className="text-white ml-1" size={26} fill="currentColor" />
          </span>
        </div>
      </div>
      <div className="pt-4">
        <span className="font-cond uppercase text-xs tracking-widest baja-red">{film.subtitle}</span>
        <h3 className="font-display text-xl uppercase text-white group-hover:baja-red transition-colors mt-1">
          {film.title}
        </h3>
      </div>
    </button>
  );
}

export default function VideoSection({
  films = FILMS,
  kicker = "Films",
  title = "SEE IT IN ACTION",
  subtitle = "Real Baja Designs installs and field tests — straight from the official channel.",
}) {
  const [active, setActive] = useState(null);

  return (
    <section id="films" className="bg-[#111] border-y border-white/10 py-24">
      <div className="max-w-[1400px] mx-auto px-5 md:px-8">
        <div className="flex items-center gap-3 mb-4">
          <span className="h-[2px] w-10 bg-baja-red" />
          <span className="font-cond uppercase tracking-[0.25em] text-sm baja-red">{kicker}</span>
        </div>
        <h2 className="font-display text-4xl md:text-6xl uppercase leading-[0.95] text-white max-w-2xl">
          {title}
        </h2>
        <p className="mt-5 text-white/60 text-lg max-w-xl">{subtitle}</p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-2 gap-6 mt-14">
          {films.map((f, i) => (
            <FilmCard key={f.id} film={f} i={i} onPlay={setActive} />
          ))}
        </div>
      </div>

      {active && (
        <div
          className="fixed inset-0 z-[60] bg-black/90 backdrop-blur flex items-center justify-center p-5"
          onClick={() => setActive(null)}
        >
          <button
            className="absolute top-6 right-6 text-white hover:text-baja-red transition-colors"
            onClick={() => setActive(null)}
            aria-label="Close"
          >
            <X size={32} />
          </button>
          <div className="w-full max-w-4xl aspect-video" onClick={(e) => e.stopPropagation()}>
            <iframe
              className="w-full h-full"
              src={`https://www.youtube.com/embed/${active.youtube}?autoplay=1&rel=0`}
              title={active.title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </section>
  );
}
